import React from 'react';
import type {
  Candidate,
  MfaPhoneNumberPreferences,
  User,
} from '@axiom/validation';
import { isEnvoyAdmin, isLpUser } from '@axiom/utils';
import {
  ParagraphHeader,
  Paragraph,
  Gutter,
  Layout,
  LayoutItem,
  Toggle,
  Form,
  ToastUtil,
  CondensedLarge,
} from '@axiom/ui';

import { CandidateCommunicationAlertsApi } from '../../api/candidate-communication-alerts-api';
import { CandidateApi } from '../../api/candidate-api';

import { PreferencesMfaPhoneActivateFormSchema } from './preferences-mfa-phone-activate-form-schema';

export type PreferencesMfaPhoneActivateProps = {
  name?: string;
  candidateId: Candidate['id'];
  mfa: MfaPhoneNumberPreferences;
  user: User;
};

export const PreferencesMfaPhoneActivate = ({
  name = 'PREFERENCES_MFA_PHONE_ACTIVATE',
  candidateId,
  mfa,
  user,
}: PreferencesMfaPhoneActivateProps) => {
  const addToast = () => {
    ToastUtil.add({
      name: '2FA_TOGGLE_RESTRICTION_TOAST',
      type: 'error',
      children: (
        <CondensedLarge>
          {mfa.registeredForMfa
            ? 'Only talent and admin can disable 2FA'
            : 'Only talent can enable 2FA'}
        </CondensedLarge>
      ),
    });
  };

  return (
    <Layout name={name} horizontalGutter="24px">
      <LayoutItem>
        <ParagraphHeader>
          I would like to turn on 2-factor authentication (2FA)
        </ParagraphHeader>
        <Gutter bottom="16px" />
        <Paragraph>
          2FA keeps your account and personal information secure by making sure
          it’s you each time you log in.
        </Paragraph>
      </LayoutItem>
      <LayoutItem>
        <div
          role="presentation"
          onClickCapture={e => {
            if (
              !isLpUser(user) &&
              !(isEnvoyAdmin(user) && mfa.registeredForMfa)
            ) {
              e.stopPropagation();
              e.preventDefault();
              addToast();
            }
          }}
        >
          <Form
            name={`${name}_FORM`}
            initialValues={{
              registeredForMfa: mfa.registeredForMfa,
            }}
            schema={PreferencesMfaPhoneActivateFormSchema}
            submitOnChange
            onSubmit={async (_changedData, _actions, values) => {
              await CandidateApi.updateRegisteredForMfa(candidateId, values);
              await CandidateApi.refreshCandidateMfaPhoneNumber(candidateId);
              await CandidateCommunicationAlertsApi.refreshCommunicationAlerts(
                candidateId
              );
            }}
          >
            {() => {
              return <Toggle name="registeredForMfa" />;
            }}
          </Form>
        </div>
      </LayoutItem>
    </Layout>
  );
};
